import React from 'react';
import Lottie from 'react-lottie';

import ImageComponent from '../../components/ImageComponent';
import type { WithdrawStatusKey } from '../../interfaces/Withdrawal';

/**
 * Timeline step icon.
 *
 * `assetUrl1` / `assetUrl2` are **Lottie JSON filenames**, not image URLs.
 * Android feeds them to `getLottieJsonFile`, which reads
 * `cacheDir/lottie/<version>/<filename>` from a bundle downloaded at boot
 * (`lottieDownloader/manager/LottieDownloadManager.kt:90-130`), and falls back
 * to a bundled vector drawable when that misses. Rendering them in an `<img>`
 * resolves e.g. `orange_blinker.json` against the PWA origin and 404s — which is
 * why both timeline rows showed a broken image.
 *
 * Resolution order here mirrors Android:
 *   1. an absolute http(s) URL  → render as an image
 *   2. a known Lottie filename  → play the bundled animation
 *   3. anything else / missing  → the static SVG fallback
 */

/* ------------------------------------------------------------------ */
/* Lottie registry                                                     */
/* ------------------------------------------------------------------ */

/**
 * The animations are not in the Android repo — only `speaker_with_ripple.json`
 * ships in `app/src/main/assets/`; the rest arrive in the lottie bundle that
 * `lottieConfigV3.assetUrl` points at (typed here as `lottieConfigV2`,
 * `src/interfaces/PrivateConfig.ts:279-282`, and never downloaded).
 *
 * To bundle one: drop it in `src/assets/lottie/` and add its import below. Until
 * then every name resolves to the SVG fallback, which is what Android shows when
 * its own lottie load fails.
 *
 *   import greenTickWithLine from '../../assets/lottie/green_tick_with_line.json';
 *   import greenCircleWithLine from '../../assets/lottie/green_circle_with_line.json';
 *   import orangeBlinker from '../../assets/lottie/orange_blinker.json';
 *   import redBlinker from '../../assets/lottie/red_blinker.json';
 */
const STEP_LOTTIES: Record<string, object> = {
  // 'green_tick_with_line.json': greenTickWithLine,
  // 'green_circle_with_line.json': greenCircleWithLine,
  // 'orange_blinker.json': orangeBlinker,
  // 'red_blinker.json': redBlinker,
};

/** Android's per-row defaults — `WithdrawStatusFragment.kt:1190-1197`. */
const GREEN_TICK_WITH_LINE = 'green_tick_with_line.json';
const ORANGE_BLINKER = 'orange_blinker.json';
const RED_BLINKER = 'red_blinker.json';

/* ------------------------------------------------------------------ */
/* Static fallbacks                                                    */
/* ------------------------------------------------------------------ */

/**
 * Ports of Android's fallback vector drawables, path-for-path:
 * `app/src/main/res/drawable/{green_dot_with_line,orange_blinker,red_blinker}.xml`.
 */

const GreenDotWithLine: React.FC = () => (
  <svg viewBox="0 0 8 51" width="8" height="51" aria-hidden="true">
    <defs>
      <linearGradient
        id="withdrawStepLine"
        x1="2.5"
        y1="4"
        x2="2.5"
        y2="44.591"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" stopColor="#1DAE34" />
        <stop offset="1" stopColor="#FFF0F0" />
      </linearGradient>
    </defs>
    <path
      d="M4,5L4,50"
      stroke="url(#withdrawStepLine)"
      strokeWidth="2"
      strokeLinecap="round"
      fill="none"
    />
    <circle cx="4" cy="4" r="3.76" fill="#1DAE34" />
  </svg>
);

/** The blinker is the same shape in both states — only the hue changes. */
const Blinker: React.FC<{ color: string }> = ({ color }) => (
  <svg viewBox="0 0 14 14" width="14" height="14" aria-hidden="true">
    <circle cx="7" cy="7" r="6.022" fill="#FFF0F0" />
    <circle
      cx="7"
      cy="7"
      r="6.022"
      fill="none"
      stroke={color}
      strokeWidth="1.04483"
    />
    <circle cx="7" cy="7" r="4.5" fill={color} stroke="#FFF0F0" strokeWidth="1" />
  </svg>
);

const FALLBACKS: Record<string, React.FC> = {
  [GREEN_TICK_WITH_LINE]: GreenDotWithLine,
  'green_circle_with_line.json': GreenDotWithLine,
  [ORANGE_BLINKER]: () => <Blinker color="#E28902" />,
  [RED_BLINKER]: () => <Blinker color="#FF3D3D" />,
};

/* ------------------------------------------------------------------ */

const isRemote = (value?: string) => Boolean(value && /^https?:\/\//i.test(value));

/**
 * The icon a row defaults to when the server names none.
 * Row 0 is always the completed step; row 1 tracks the outcome, turning red on
 * failure exactly as `WithdrawStatusFragment.kt:1143-1145` does.
 */
function defaultAsset(index: number, status?: WithdrawStatusKey): string {
  if (index === 0) return GREEN_TICK_WITH_LINE;
  return status === 'fail' ? RED_BLINKER : ORANGE_BLINKER;
}

interface IStepIconProps {
  assetUrl?: string;
  /** Position in the timeline — decides the default when `assetUrl` is absent. */
  index: number;
  status?: WithdrawStatusKey;
  className?: string;
}

const StepIcon: React.FC<IStepIconProps> = ({
  assetUrl,
  index,
  status,
  className,
}) => {
  if (isRemote(assetUrl)) {
    return (
      <ImageComponent src={assetUrl as string} alt="" className={className} />
    );
  }

  const name = assetUrl || defaultAsset(index, status);
  const animationData = STEP_LOTTIES[name];

  if (animationData) {
    return (
      <div className={className}>
        <Lottie
          options={{ loop: true, autoplay: true, animationData }}
          height="100%"
          width="100%"
        />
      </div>
    );
  }

  // Unknown name, or the animation is not bundled yet.
  const Fallback = FALLBACKS[name] || FALLBACKS[defaultAsset(index, status)];
  return <span className={className}>{Fallback ? <Fallback /> : null}</span>;
};

export default StepIcon;
